import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

function isNewSupabaseApiKey(value: string): boolean {
  return value.startsWith("sb_publishable_") || value.startsWith("sb_secret_");
}

function buildServerPublicClient() {
  const url = process.env.SUPABASE_URL;
  const key = process.env.SUPABASE_PUBLISHABLE_KEY;
  if (!url || !key) throw new Error("supabase_env_missing");
  return createClient<Database>(url, key, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const headers = new Headers(init?.headers);
        if (
          isNewSupabaseApiKey(key) &&
          headers.get("Authorization") === `Bearer ${key}`
        ) {
          headers.delete("Authorization");
        }
        headers.set("apikey", key);
        return fetch(input, { ...init, headers });
      },
    },
  });
}

export type PublicArticleDTO = {
  id: string;
  slug: string;
  category: string;
  title: string;
  excerpt: string;
  body: string[];
  imageUrl: string | null;
  altText: string | null;
  author: string;
  readTime: string;
  publishedAt: string | null;
  isDemo: boolean;
  sortOrder: number;
};

const SELECT_COLS =
  "id, slug, category, title, excerpt, body, image_url, alt_text, author, read_time, published_at, is_demo, sort_order";

function mapRow(r: {
  id: string;
  slug: string;
  category: string;
  title: string;
  excerpt: string;
  body: string[];
  image_url: string | null;
  alt_text: string;
  author: string;
  read_time: string;
  published_at: string | null;
  is_demo: boolean;
  sort_order: number;
}): PublicArticleDTO {
  return {
    id: r.id,
    slug: r.slug,
    category: r.category,
    title: r.title,
    excerpt: r.excerpt,
    body: r.body ?? [],
    imageUrl: r.image_url,
    altText: r.alt_text ?? null,
    author: r.author,
    readTime: r.read_time,
    publishedAt: r.published_at,
    isDemo: r.is_demo,
    sortOrder: r.sort_order,
  };
}

/**
 * Public unauthenticated list. Returns null on ANY error so the caller can
 * fall back to the bundled articles. Empty successful result is [].
 */
export const listPublishedArticles = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicArticleDTO[] | null> => {
    try {
      const supabase = buildServerPublicClient();
      const nowIso = new Date().toISOString();
      const { data, error } = await supabase
        .from("journal_articles")
        .select(SELECT_COLS)
        .eq("status", "published")
        .eq("is_demo", false)
        .not("published_at", "is", null)
        .lte("published_at", nowIso)
        .order("sort_order", { ascending: true })
        .order("published_at", { ascending: false, nullsFirst: false })
        .order("created_at", { ascending: false });
      if (error) {
        console.error("[journal] list read failed", error);
        return null;
      }
      return (data ?? []).map(mapRow);
    } catch (err) {
      console.error("[journal] list unexpected error", err);
      return null;
    }
  },
);

export type PublicArticleResult =
  | { kind: "found"; article: PublicArticleDTO }
  | { kind: "not_found" }
  | { kind: "error" };

export const getPublishedArticleBySlug = createServerFn({ method: "GET" })
  .inputValidator((raw: { slug: string }) => ({ slug: String(raw.slug ?? "").trim() }))
  .handler(async ({ data }): Promise<PublicArticleResult> => {
    if (!data.slug) return { kind: "not_found" };
    try {
      const supabase = buildServerPublicClient();
      const nowIso = new Date().toISOString();
      const { data: row, error } = await supabase
        .from("journal_articles")
        .select(SELECT_COLS)
        .eq("slug", data.slug)
        .eq("status", "published")
        .eq("is_demo", false)
        .not("published_at", "is", null)
        .lte("published_at", nowIso)
        .maybeSingle();
      if (error) {
        console.error("[journal] detail read failed", error);
        return { kind: "error" };
      }
      if (!row) return { kind: "not_found" };
      return { kind: "found", article: mapRow(row) };
    } catch (err) {
      console.error("[journal] detail unexpected error", err);
      return { kind: "error" };
    }
  });